/**
 * Page taxonomy → page context. Each site ships a page-taxonomy.json (rules
 * first-match-wins, per-path overrides, cluster regexes); this resolves a path
 * to { intent, stage, type, cluster, module } and exposes it to analytics.
 */

export interface PageContext {
  page_intent: string
  page_stage: string
  page_type: string
  page_cluster: string
  page_module: string
  taxonomy_version: number
}

export interface PageTaxonomy {
  version: number
  clusters: [string, string][]
  modules: Record<string, string>
  rules: ({ match: string } & Partial<Record<"intent" | "stage" | "type" | "cluster", string>>)[]
  overrides: Record<string, Partial<Record<"intent" | "stage" | "type" | "cluster", string>>>
}

const norm = (path: string) => {
  const p = (path || "/").split(/[?#]/)[0]
  return p.endsWith("/") ? p : p + "/"
}

export function resolvePageContext(tax: PageTaxonomy, rawPath: string): PageContext {
  const path = norm(rawPath)
  const rule = tax.rules.find(r => new RegExp(r.match).test(path)) || {}
  const o = { ...rule, ...(tax.overrides[path] || {}) } as Partial<Record<string, string>>
  const cluster = o.cluster || tax.clusters.find(([, re]) => new RegExp(re).test(path))?.[0] || "platform"
  return {
    page_intent: o.intent || "informational",
    page_stage: o.stage || "awareness",
    page_type: o.type || "guide",
    page_cluster: cluster,
    page_module: tax.modules[cluster] || "platform",
    taxonomy_version: tax.version,
  }
}

/** Inline <head> script: puts the context on the dataLayer before GTM's first page_view. */
export function pageContextScript(ctx: PageContext): string {
  return `window.dataLayer=window.dataLayer||[];window.dataLayer.push(${JSON.stringify({ event: "page_context", ...ctx })});`
}

/** Client-side navigation: push before the route change so GTM's history page_view sees the new page. */
export function pushPageContext(tax: PageTaxonomy, path: string): PageContext | null {
  if (typeof window === "undefined") return null
  const ctx = resolvePageContext(tax, path)
  const w = window as unknown as { dataLayer?: unknown[] }
  w.dataLayer = w.dataLayer || []
  w.dataLayer.push({ event: "page_context", ...ctx })
  return ctx
}
